import { concat, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import EmberTooltip from 'ember-tooltips/components/ember-tooltip';
import LineClamp from 'garaje/components/line-clamp';
import UiButton from 'garaje/components/ui-button';
import dashboardFieldName from 'garaje/helpers/dashboard-field-name';

export type SortDirection = 'asc' | 'desc';

interface SortableHeaderColumnSignature {
  Args: {
    name: string;
    sortAction: (sortField: string, direction: SortDirection) => void;
    isActive: boolean;
    sortDirection: SortDirection;
    sortField?: string;
  };
  Blocks: {
    'after-header-name'?: [];
  };
}

export default class SortableHeaderColumn extends Component<SortableHeaderColumnSignature> {
  @tracked nameWasTruncated = false;

  get isAsc(): boolean {
    const { isActive, sortDirection } = this.args;
    return isActive && sortDirection === 'asc';
  }

  get isDesc(): boolean {
    const { isActive, sortDirection } = this.args;
    return isActive && sortDirection === 'desc';
  }

  @action
  toggleSortAction(): void {
    const { isActive, sortDirection } = this.args;
    let dir: 'asc' | 'desc' = 'asc';
    if (isActive && sortDirection === 'asc') {
      dir = 'desc';
    }
    this.args.sortAction(this.args.sortField!, dir);
  }

  <template>
    {{#if @sortField}}
      <div class="inline-block">
        <div data-test-sortable-header={{dashboardFieldName @name}} class="flex">
          <div style="align-self: center">
            <UiButton
              class={{concat "sort-field " (if @isActive "active")}}
              {{on "click" this.toggleSortAction}}
              data-test-header
              data-test-sortable-col-header-active={{if @isActive true false}}
            >
              <span class="sr-only">Sort by</span>
              <span data-test-field-header class="text-sm text-carbon-50">
                <LineClamp
                  @handleTruncate={{fn (mut this.nameWasTruncated)}}
                  @lines={{2}}
                  @lineHeight={{1.25}}
                  @text={{dashboardFieldName @name}}
                />
              </span>
            </UiButton>
          </div>

          {{yield to="after-header-name"}}

          <div class="ml-2 flex flex-col" style="align-self:center">
            <UiButton
              data-test-asc-button
              {{on "click" (fn @sortAction @sortField "asc")}}
              class={{concat "sort-asc mb-0.5 " (if this.isAsc "active")}}
            >
              <span class="sr-only">sort in ascending order</span>
            </UiButton>
            <UiButton
              data-test-desc-button
              {{on "click" (fn @sortAction @sortField "desc")}}
              class={{concat "sort-desc mb-0.5 " (if this.isDesc "active")}}
            >
              <span class="sr-only">sort in descending order</span>
            </UiButton>
          </div>
        </div>
      </div>
    {{else}}
      <div style="align-items: center;" class="flex flex-row text-sm">
        <span data-test-field-header>
          <LineClamp
            @handleTruncate={{fn (mut this.nameWasTruncated)}}
            @lines={{2}}
            @lineHeight={{1.25}}
            @text={{dashboardFieldName @name}}
          />
        </span>
      </div>
    {{/if}}

    {{#if this.nameWasTruncated}}
      <EmberTooltip @tooltipClass="break-all" @delay={{400}} @side="top" @text={{dashboardFieldName @name}} />
    {{/if}}
  </template>
}
